import { Composable, Inject, Vue } from '@natureos/vue-facing-decorator';
import type { Feature, FeatureCollection } from 'geojson';

import {
  calculateAreaSizeInHa,
  DEGENERATE_AREA_HA,
  formatDate,
  isPolygonalGeometry,
  normalizeToFeature,
  type PolygonFeature,
} from '@/common/helpers';
import type { ApiService, ApiResponse } from '@/common/services/api.service-new';
import { AggregateCoverage } from './aggregate-coverage';
import {
  EXPLORATION_ZONE_MIN_ID,
  isAoiLayer,
  isStagingZone,
  type Multizone,
  type MultizoneWithZonesData,
  type Zone,
} from './zone.types';

/**
 * The `zonetype` query param for a parent-zone read, chosen by id band: an
 * exploration `zone:04` (>= 10M) is read with `zonetype=04`, everything else as
 * a `zone:02` project. The BE returns the same `JoinedZone` shape for both, so
 * the only difference is this discriminator.
 */
const zonetypeForZoneId = (zoneId: number): '02' | '04' =>
  zoneId >= EXPLORATION_ZONE_MIN_ID ? '04' : '02';

/** A zone freshly created via a write endpoint (`/zones/store`, `/zones/explore`). */
export interface CreatedZone {
  zone: number;
  type: string;
  name: string;
}

/** `/projects` resolves every child zone in one browser request. */
interface ProjectWithResolvedZones {
  zone: number;
  zones: Zone[];
}

/** Minimal slice of a zone's `/layers` response — enough to resolve a layer-sourced AOI geometry. */
interface ZoneLayerInfo {
  name: string;
  date: string | null;
  /** Full BE sort key `layer:<source>:<canonical_key>:<date>` — carries the stable AOI marker. */
  layer_id?: string;
  source: { type?: string; url: string | null };
}


/** A zone's boundary as one polygon, or `null` when it has no usable geometry. */
function zoneBoundaryFeature(zone: Zone): PolygonFeature | null {
  if (!zone.geojson) {
    return null;
  }
  try {
    const feature = normalizeToFeature(zone.geojson);
    return feature && isPolygonalGeometry(feature.geometry) ? (feature as PolygonFeature) : null;
  } catch {
    return null;
  }
}

/**
 * A zone is "degenerate" when its backend *geometry* has no usable area — its
 * real boundary must come from an `aoi` layer.
 *
 * Measured off the geometry, never off `zone.areaSize`: callers patch `areaSize`
 * for display from other sources (the multizone aggregate, for one) before this
 * runs, and an area-based test would then read "healthy" while the geometry every
 * map and zonal-stats AOI uses is still the placeholder. Zones fetched without
 * geometry fall back to the reported area — there is nothing to repair from a
 * boundary we were never given.
 *
 * Deliberately the summed area of the raw collection rather than a normalised
 * union: the two only differ for overlapping parcels, never anywhere near a
 * ~0-area placeholder, and this runs for every zone of every project card.
 */
const isDegenerateZone = (zone: Zone): boolean => {
  if (!zone.geojson) {
    return !zone.areaSize || zone.areaSize < DEGENERATE_AREA_HA;
  }
  try {
    return calculateAreaSizeInHa(zone.geojson) < DEGENERATE_AREA_HA;
  } catch {
    return true;
  }
};

/**
 * Whether an aggregate feature carries this child's id. Kept separate from the
 * name fallback because a name is not unique — the RDS import path stamps only
 * `{name}`, so two children uploaded under one name are indistinguishable by it.
 * An id match anywhere in the collection must therefore beat a name match.
 */
const matchesZoneId = (feature: Feature, zone: Pick<Zone, 'zone'>): boolean => {
  const properties = feature.properties ?? {};
  const zoneId = String(zone.zone);
  return [properties.zone_id, properties.zone, properties.id].some(
    (value) => value !== null && value !== undefined && String(value) === zoneId,
  );
};

/** Match the child-zone identifiers emitted by the different multizone API shapes. */
export const matchesZoneFeature = (feature: Feature, zone: Pick<Zone, 'zone' | 'name'>): boolean =>
  matchesZoneId(feature, zone) || feature.properties?.name === zone.name;

/** Index of the aggregate feature describing `zone`, preferring an id match over the ambiguous name fallback. */
const findZoneFeatureIndex = (features: Feature[], zone: Pick<Zone, 'zone' | 'name'>): number => {
  const byId = features.findIndex((feature) => matchesZoneId(feature, zone));
  return byId >= 0 ? byId : features.findIndex((feature) => feature.properties?.name === zone.name);
};

@Composable()
export class useZoneService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  public async getZoneById(zoneId: number, includeGeojson: boolean = true): Promise<ApiResponse<Zone>> {
    const res = await this.api.getList<Zone>(
      `${import.meta.env.VITE_ZONES_API}/zones?zone_id=${zoneId}&include_geojson=${includeGeojson}`,
    );

    const response: ApiResponse<Zone> = {
      data: null,
      error: res.error,
      isSuccess: res.isSuccess,
    };

    if (res.data?.length) {
      response.data = res.data[0];

      if (response.data?.geojson) {
        response.data.areaSize = calculateAreaSizeInHa(response.data.geojson);
      }
    }

    return response;
  }

  // Lightweight zone fetch via the singular `/zones/{zone_id}` endpoint, which
  // (unlike `/zones?zone_id=X`) projects `client_id` correctly and so honours
  // `?include_geojson=false` without 500-ing. Use this when you only need
  // metadata (name, zone, etc.) and want to skip the GeoJSON payload.
  public async getZoneSummary(zoneId: number): Promise<ApiResponse<Zone>> {
    return this.api.get<Zone>(
      `${import.meta.env.VITE_ZONES_API}/zones/${zoneId}?include_geojson=false`,
    );
  }

  public async getMultizoneById(multizoneId: number, includeGeojson: boolean = true) {
    const res = await this.api.getList<Multizone>(
      `${import.meta.env.VITE_ZONES_API}/zones?zonetype=${zonetypeForZoneId(multizoneId)}&zone_id=${multizoneId}&include_geojson=${includeGeojson}`,
    );

    const data = res.data?.[0] ?? null;

    return { ...res, data };
  }

  /**
   * Create an empty ephemeral exploration project (`zone:04`) via interface-tiler
   * `POST /zones/explore`. Drawn geometry is attached afterwards as `zone:03`
   * children through the existing `POST /zones/store` (with this id as
   * `project_id`). Any authenticated user; owner-scoped server-side. Per the
   * `ephemeral-exploration-zone` contract.
   */
  public createExplorationZone(name: string, description: string | null = null): Promise<ApiResponse<CreatedZone>> {
    return this.api.post<CreatedZone>(`${import.meta.env.VITE_ZONES_API}/zones/explore`, { name, description });
  }

  /** The current user's saved explorations (`zone:04`), names only — for the home "Explorations" list. */
  public getExplorations() {
    return this.api.getList<Multizone>(`${import.meta.env.VITE_ZONES_API}/zones?zonetype=04&include_geojson=false`);
  }

  /** Delete an exploration (`zone:04`) and cascade its `zone:03` children. Owner-gated server-side. */
  public deleteExploration(zoneId: number): Promise<ApiResponse<void>> {
    return this.api.delete<void>(`${import.meta.env.VITE_ZONES_API}/zones/explore/${zoneId}`);
  }

  public getMultizones() {
    // include_geojson=false drops 2.2MB→7KB on this listing call. Home page
    // cards lazy-load their own per-multizone geojson when they mount; the
    // notification-settings picker only needs names.
    return this.api.getList<Multizone>(`${import.meta.env.VITE_ZONES_API}/zones?zonetype=02&include_geojson=false`);
  }

  public async getMultizoneWithZonesDataById(multizoneId: number, includeGeojson: boolean = true) {
    const multizoneRequest = this.api.getList<Multizone>(
      `${import.meta.env.VITE_ZONES_API}/zones?zonetype=${zonetypeForZoneId(multizoneId)}&zone_id=${multizoneId}&include_geojson=${includeGeojson}`,
    );
    // `/projects` resolves all zone:02 children server-side, avoiding one
    // browser request per child. Saved explorations are zone:04 and are not
    // served by that endpoint, so they retain the per-child path below.
    const projectRequest = multizoneId < EXPLORATION_ZONE_MIN_ID
      ? this.api.getList<ProjectWithResolvedZones>(
          `${import.meta.env.VITE_ZONES_API}/projects?project_id=${multizoneId}&include_geojson=${includeGeojson}`,
        )
      : Promise.resolve(null);

    const [res, projectRes] = await Promise.all([multizoneRequest, projectRequest]);
    const multizone = res.data?.[0] ?? null;

    if (!multizone) {
      return { ...res, data: null };
    }

    const projectZones = projectRes?.data?.find((project) => project.zone === multizoneId)?.zones;
    let zonesData = Array.isArray(projectZones) ? projectZones : null;
    if (!zonesData) {
      const zonesResponses = await Promise.all(
        multizone.zones.map((zoneId) => this.getZoneById(zoneId, includeGeojson)),
      );
      zonesData = zonesResponses.filter((response) => response.data).map((response) => response.data) as Zone[];
    } else if (includeGeojson) {
      for (const zone of zonesData) {
        if (zone.geojson) {
          zone.areaSize = calculateAreaSizeInHa(zone.geojson);
        }
      }
    }

    const data: MultizoneWithZonesData = { ...multizone, zonesData };

    return { ...res, data };
  }

  public getCollectionFeatures(collectionUrl: string, date: string) {
    return this.api.get<FeatureCollection>(
      `${import.meta.env.VITE_VECTOR_TILER_API}/collections/${collectionUrl}/items?f=geojson&limit=1000&date=${date}`,
    );
  }

  /** A zone's published layers; `null` for none (204) or a malformed body. */
  public getZoneLayers(zoneId: number): Promise<ApiResponse<ZoneLayerInfo[]>> {
    return this.api.getList<ZoneLayerInfo>(`${import.meta.env.VITE_ZONES_API}/zones/${String(zoneId)}/layers`);
  }

  /**
   * Make a multizone's child geometries — and the aggregate outline built from
   * them — usable. Two independent defects are repaired here:
   *
   * 1. Some zones (those whose real boundary is a polygon *with holes*) can't be
   *    represented by the zones backend, which returns a degenerate placeholder
   *    geometry whose area is ~0. Their true boundary lives in an `aoi`
   *    vector-tile layer, so `zone.geojson` / `zone.areaSize` are patched from it.
   * 2. The aggregate `multizoneGeojson` is assembled independently by the
   *    backend and can be stale for a child whose own `/zones` geometry is
   *    perfectly fine — observed on Revive Borneo, where Blok 1 (zone 1927)
   *    returns 39,377 ha of its own but is a ~0.15 m² placeholder in the
   *    aggregate. That is not cosmetic: the aggregate IS the multizone-scope
   *    zonal-stats AOI, so every per-COG request for that child clipped to
   *    nothing and the whole project histogram silently fell back.
   *
   * Which is why (2) reconciles **every** child, not just the ones (1) repaired:
   * the aggregate is treated as derived from the child geometries, never as an
   * independent source of truth.
   *
   * Shared by the zone-details view, the home overview map, and the home project
   * cards so the resolution logic lives in exactly one place.
   */
  public async resolveDegenerateZones(zones: Zone[], multizoneGeojson?: FeatureCollection | null): Promise<void> {
    // Staging zones (zone:03) carry their drawn geometry inline and have no `aoi`
    // layer — plot them straight from `geojson`, never via AOI resolution (which
    // would fire a fruitless `/layers` fetch and leave them boundary-less).
    const degenerateZones = zones.filter((zone) => isDegenerateZone(zone) && !isStagingZone(zone));

    await Promise.all(
      degenerateZones.map(async (zone) => {
        try {
          const { data: layers } = await this.getZoneLayers(zone.zone);
          const aoiLayer = layers?.find((layer) => isAoiLayer(layer) && layer.source?.url && layer.date);

          if (!aoiLayer) {
            return;
          }

          const { data } = await this.getCollectionFeatures(aoiLayer.source.url!, formatDate(aoiLayer.date!));

          if (!data?.features?.length) {
            return;
          }

          const areaHa = calculateAreaSizeInHa(data);

          if (areaHa < DEGENERATE_AREA_HA) {
            return;
          }

          zone.areaSize = areaHa;
          zone.geojson = data;
        } catch (err) {
          console.warn(`[aoi-resolver] Failed to resolve AOI geometry for zone ${zone.zone}:`, err);
        }
      }),
    );

    // An EMPTY aggregate is left to the caller's own child-assembly fallback
    // (`details.view.assembleMultizoneGeojsonFromZones`), which preserves
    // per-feature properties this cannot reconstruct.
    if (multizoneGeojson?.features?.length) {
      this.reconcileMultizoneFeatures(zones, multizoneGeojson);
    }
  }

  /**
   * Bring each child zone's boundary into the aggregate FeatureCollection.
   *
   * The test is whether the aggregate ALREADY COVERS the child's ground, not
   * whether some feature carries the child's id. Identifiers are unreliable —
   * the promote path merges a child's features into the aggregate stamping no
   * id at all, and the RDS import path stamps only a (non-unique) name — so
   * keying off them would append a duplicate of a child that is already present,
   * and `calculateAreaSizeInHa` SUMS a FeatureCollection: a duplicated child
   * silently doubles the reported project hectares. Coverage is the property
   * every consumer actually depends on.
   *
   * Only ever *widens* the aggregate towards the child geometries: a collection
   * that already covers its children is left byte-for-byte alone, so this is a
   * no-op for healthy multizones.
   */
  private reconcileMultizoneFeatures(zones: Zone[], multizoneGeojson: FeatureCollection): void {
    // Edits go through `coverage`, which keeps its index in step: reconciling
    // one child changes what the aggregate covers for the next.
    const coverage = new AggregateCoverage(multizoneGeojson.features);

    for (const zone of zones) {
      const zoneFeature = zoneBoundaryFeature(zone);

      if (!zoneFeature) {
        continue;
      }

      const zoneAreaHa = calculateAreaSizeInHa(zoneFeature);

      if (zoneAreaHa < DEGENERATE_AREA_HA || coverage.covers(zoneFeature, zoneAreaHa)) {
        continue;
      }

      const featureIndex = findZoneFeatureIndex(multizoneGeojson.features, zone);
      const existingFeature = featureIndex >= 0 ? multizoneGeojson.features[featureIndex] : null;
      // Typed as PolygonFeature, not Feature: the geometry is copied from the
      // already-validated `zoneFeature`, and keeping that proof means a future
      // non-polygon assignment fails at compile time.
      const reconciledFeature: PolygonFeature = {
        type: 'Feature',
        geometry: zoneFeature.geometry,
        // Keep whatever identifiers the aggregate already carried (consumers key
        // off them) and add the ones our own matcher relies on.
        properties: {
          ...existingFeature?.properties,
          id: String(zone.zone),
          // `details.view` matches children numerically on `zone_id`; keep it so a
          // feature we appended is still found by that pass.
          zone_id: zone.zone,
          name: zone.name,
        },
      };

      if (featureIndex >= 0) {
        // REPLACEMENT: the old shape must leave the index too, or a later child
        // would pass the coverage check against phantom geometry.
        coverage.replace(featureIndex, reconciledFeature);
      } else {
        coverage.append(reconciledFeature);
      }
    }
  }

  public async getMultizonesWithZonesData(includeGeojson: boolean = true) {
    // Child geojsons flow via the per-zone fetches below; the outer listing
    // stays geometry-free (see `getMultizones`).
    const res = await this.getMultizones();

    if (res.data) {
      const multizonesWithData = await Promise.all(
        res.data.map(async (multizone) => {
          const zonesPromises = multizone.zones.map((zoneId) => this.getZoneById(zoneId, includeGeojson));
          const zonesResponses = await Promise.all(zonesPromises);

          const zonesData = zonesResponses.filter((res) => res.data).map(({ data }) => data) as Zone[];

          return {
            ...multizone,
            zonesData,
          } as MultizoneWithZonesData;
        }),
      );

      return { data: multizonesWithData } as ApiResponse<MultizoneWithZonesData[]>;
    }

    return res as ApiResponse<MultizoneWithZonesData[]>;
  }
}
